import React, { useRef, useState } from 'react';

import IconButton from '@atlaskit/button/icon/button';
import DropdownItemRadio from '@atlaskit/dropdown-menu/dropdown-item-radio';
import DropdownItemRadioGroup from '@atlaskit/dropdown-menu/dropdown-item-radio-group';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import mergeRefs from '@atlaskit/ds-lib/merge-refs';
import { useId } from '@atlaskit/ds-lib/use-id';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import PanelRightIcon from '@atlaskit/icon/core/panel-right';
import { Inline } from '@atlaskit/primitives/compiled/inline';

export interface PanelViewOption {
	/**
	 * Unique value identifying this view.
	 */
	value: string;
	/**
	 * Localized name displayed in the menu.
	 */
	label: string;
	/**
	 * Optional icon displayed before the menu label and in the trigger when selected.
	 */
	icon?: React.ComponentProps<typeof IconButton>['icon'];
	/**
	 * Prevents selection of an unavailable view.
	 */
	isDisabled?: boolean;
}

export interface PanelActionViewSwitcherProps {
	/**
	 * Available views, in display order. Values must be unique.
	 */
	views: readonly PanelViewOption[];
	/**
	 * The current view, controlled by the consuming experience.
	 */
	selectedView: string;
	/**
	 * Called when a different view is selected. The consumer owns the layout change.
	 */
	onViewChange: (value: string) => void;
	/**
	 * Localized accessible label and tooltip for the trigger.
	 */
	label?: string;
	/**
	 * Localized heading and accessible name for the menu.
	 */
	title?: string;
	/**
	 * URL to open in a new tab. Omit to hide the New tab action.
	 */
	newTabHref?: string;
	/**
	 * Localized label for the New tab action.
	 */
	newTabLabel?: string;
	/**
	 * Unique identifier used by automated tests.
	 */
	testId?: string;
}

/**
 * A panel header action for selecting one view, such as floating, sidebar, or full screen.
 * Consumers own view state and any focus transfer when changing layouts unmounts the trigger.
 */
export function PanelActionViewSwitcher({
	views,
	selectedView,
	onViewChange,
	label = 'Switch view',
	title = 'Switch view',
	newTabHref,
	newTabLabel = 'New tab',
	testId,
}: PanelActionViewSwitcherProps): React.JSX.Element {
	const [isOpen, setIsOpen] = useState(false);
	const id = useId();
	const buttonRef = useRef<HTMLButtonElement>(null);

	const selectedOption = views.find((view) => view.value === selectedView);

	return (
		<DropdownMenu
			shouldRenderToParent
			isOpen={isOpen}
			onOpenChange={({ isOpen }) => setIsOpen(isOpen)}
			placement="bottom-end"
			menuLabel={title}
			testId={testId}
			trigger={({ triggerRef, ...triggerProps }) => (
				<IconButton
					{...triggerProps}
					ref={mergeRefs([triggerRef, buttonRef])}
					icon={selectedOption?.icon ?? PanelRightIcon}
					label={label}
					appearance="subtle"
					spacing="compact"
					isTooltipDisabled={false}
				/>
			)}
		>
			<DropdownItemRadioGroup id={`panel-views-${id}`} title={title}>
				{views.map(({ icon: ViewIcon, ...view }) => (
					<DropdownItemRadio
						key={view.value}
						id={`panel-views-${id}-${view.value}`}
						isSelected={view.value === selectedView}
						isDisabled={view.isDisabled}
						onClick={() => {
							setIsOpen(false);
							buttonRef.current?.focus();
							if (view.value !== selectedView) {
								onViewChange(view.value);
							}
						}}
					>
						<Inline as="span" space="space.100" alignBlock="center">
							{ViewIcon && <ViewIcon label="" />}
							{view.label}
						</Inline>
					</DropdownItemRadio>
				))}
			</DropdownItemRadioGroup>
			{newTabHref && (
				<DropdownItemGroup hasSeparator>
					<DropdownItem
						href={newTabHref}
						target="_blank"
						rel="noopener noreferrer"
						elemBefore={<LinkExternalIcon label="" />}
						onClick={() => {
							setIsOpen(false);
							buttonRef.current?.focus();
						}}
					>
						{newTabLabel}
					</DropdownItem>
				</DropdownItemGroup>
			)}
		</DropdownMenu>
	);
}
